import React, { useEffect, useState } from 'react';
import { useTravelBytesGenerator } from '@/hooks/useTravelBytesGenerator';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { Sparkles, CheckCircle, Loader2, AlertCircle } from 'lucide-react';
import { format } from 'date-fns';
import { ScrollArea } from '@/components/ui/scroll-area';

export function TravelBytesGenerator() {
  const { 
    blogPosts, 
    isLoadingPosts, 
    isGenerating, 
    fetchBlogPosts, 
    generateTravelBytes 
  } = useTravelBytesGenerator();
  
  const [selectedPosts, setSelectedPosts] = useState<Set<string>>(new Set());

  useEffect(() => {
    fetchBlogPosts();
  }, []);

  const handleSelectPost = (postId: string, checked: boolean) => {
    const newSelection = new Set(selectedPosts);
    if (checked) {
      newSelection.add(postId);
    } else {
      newSelection.delete(postId);
    }
    setSelectedPosts(newSelection);
  };

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      // Select all unprocessed posts
      const unprocessedIds = blogPosts
        .filter(post => !post.has_generated)
        .map(post => post.id);
      setSelectedPosts(new Set(unprocessedIds));
    } else {
      setSelectedPosts(new Set());
    }
  };

  const handleGenerate = async () => {
    if (selectedPosts.size === 0) return;
    
    try {
      await generateTravelBytes(Array.from(selectedPosts));
      setSelectedPosts(new Set()); // Clear selection after successful generation
    } catch (error) {
      // Error handling is done in the hook
    }
  };

  const unprocessedCount = blogPosts.filter(p => !p.has_generated).length;
  const allUnprocessedSelected = unprocessedCount > 0 && 
    blogPosts.filter(p => !p.has_generated).every(p => selectedPosts.has(p.id));

  return (
    <Card className="mb-6">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-primary" />
              AI Travel Bytes Generator
            </CardTitle>
            <CardDescription>
              Automatically extract engaging travel bytes from your blog posts using AI
            </CardDescription>
          </div>
          <Badge variant="secondary">
            {selectedPosts.size} selected
          </Badge>
        </div>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {/* Info Box */}
          <div className="rounded-lg bg-muted/50 p-4 space-y-2">
            <div className="flex items-start gap-2">
              <AlertCircle className="h-4 w-4 mt-0.5 text-muted-foreground" />
              <div className="text-sm space-y-1">
                <p className="font-medium">How it works:</p>
                <ul className="list-disc list-inside text-muted-foreground space-y-1 ml-2">
                  <li>AI analyzes each blog post and extracts 5-7 compelling Travel Bytes</li>
                  <li>Each generation costs ~1 AI request per blog post</li>
                  <li>Generated content is saved as drafts for your review</li>
                  <li>You have {unprocessedCount} unprocessed blog posts</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Controls */}
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <Checkbox
                id="select-all"
                checked={allUnprocessedSelected}
                onCheckedChange={handleSelectAll}
                disabled={isLoadingPosts || unprocessedCount === 0}
              />
              <label
                htmlFor="select-all"
                className="text-sm font-medium leading-none cursor-pointer"
              >
                Select all unprocessed ({unprocessedCount})
              </label>
            </div>
            <Button
              onClick={handleGenerate}
              disabled={selectedPosts.size === 0 || isGenerating}
              size="sm"
            >
              {isGenerating ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Generating...
                </>
              ) : (
                <>
                  <Sparkles className="mr-2 h-4 w-4" />
                  Generate Travel Bytes
                </>
              )}
            </Button>
          </div>

          {/* Blog Posts List */}
          <ScrollArea className="h-[400px] rounded-md border">
            <div className="p-4 space-y-2">
              {isLoadingPosts ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                </div>
              ) : blogPosts.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  No published blog posts found
                </div>
              ) : (
                blogPosts.map((post) => (
                  <div
                    key={post.id}
                    className={`flex items-start space-x-3 rounded-lg border p-3 transition-colors ${
                      post.has_generated ? 'bg-muted/30' : 'hover:bg-accent'
                    }`}
                  >
                    <Checkbox
                      id={post.id}
                      checked={selectedPosts.has(post.id)}
                      onCheckedChange={(checked) => handleSelectPost(post.id, checked as boolean)}
                      disabled={post.has_generated}
                    />
                    <div className="flex-1 space-y-1">
                      <label
                        htmlFor={post.id}
                        className={`text-sm font-medium leading-none cursor-pointer ${
                          post.has_generated ? 'text-muted-foreground' : ''
                        }`}
                      >
                        {post.title}
                      </label>
                      <div className="flex items-center gap-2 text-xs text-muted-foreground">
                        <span>{format(new Date(post.created_at), 'MMM d, yyyy')}</span>
                        <span>•</span>
                        <span>{post.slug}</span>
                      </div>
                    </div>
                    {post.has_generated && (
                      <Badge variant="outline" className="shrink-0">
                        <CheckCircle className="mr-1 h-3 w-3" />
                        Processed ({post.generated_count || 0})
                      </Badge>
                    )}
                  </div>
                ))
              )}
            </div>
          </ScrollArea>

          {/* Credit Info */}
          <div className="text-xs text-muted-foreground">
            💡 Tip: Each blog post costs ~1 AI request. You can manage credits in Settings → Workspace → Usage.
          </div>
        </div>
      </CardContent>
    </Card>
  );
}